import { useRef, useEffect, useEffectEvent } from 'react';
import { useDidUpdate } from '@mantine/hooks';
import { type UseQueryResult } from '@tanstack/react-query';
import { InterceptedError } from '@/shared/api/intercepted-error';
import { useDeepEffect } from '@/shared/lib/hooks/use-deep-effect';

type ResolverCallbacks<T> = {
  onSuccess?: (data: T) => void;
  onError?: (error: unknown) => void;
  onSettled?: (data?: T, error?: unknown) => void;

  refetchable?: boolean;
};

export const useQueryResolver = <T = unknown, E = InterceptedError>(
  query: UseQueryResult<T, E>,
  {
    onSuccess: _onSuccess,
    onError: _onError,
    onSettled: _onSettled,
    refetchable = true,
  }: ResolverCallbacks<T> = {},
) => {
  const { isSuccess, isError, isFetched, data, error, dataUpdatedAt } = query;

  const calledSuccessRef = useRef(false);

  const onSuccess = useEffectEvent((payload: T) => {
    _onSuccess?.(payload);
  });

  const onError = useEffectEvent((err: unknown) => {
    _onError?.(err);
  });

  const onSettled = useEffectEvent((payload?: T, err?: unknown) => {
    _onSettled?.(payload, err);
  });

  // Reset success guard on new data (refetch of same key)
  useDidUpdate(() => {
    if (!refetchable) return;
    calledSuccessRef.current = false;
  }, [dataUpdatedAt, refetchable]);

  // Reset success guard on query key change (isFetched goes false for new query)
  useEffect(() => {
    if (!isFetched) {
      calledSuccessRef.current = false;
    }
  }, [isFetched]);

  useDeepEffect(() => {
    if (!isFetched) return;

    if (isSuccess && data !== undefined) {
      if (!calledSuccessRef.current) {
        onSuccess?.(data);
        calledSuccessRef.current = true;
      }
      onSettled?.(data, undefined);
      return;
    }

    if (isError && error !== undefined) {
      onError?.(error);
      onSettled?.(undefined, error);
    }
  }, [isSuccess, isError, isFetched, data, error, dataUpdatedAt]);

  return query;
};

type MultiResolverCallbacks<T> = {
  // Per-query callbacks
  onSuccess?: (data: T, index: number) => void;
  onError?: (error: unknown, index: number) => void;
  onSettled?: (data?: T, error?: unknown, index?: number) => void;
  // All-queries aggregate callbacks
  onAllSuccess?: (data: T[]) => void;
  onAllError?: (errors: unknown[]) => void;
  onAllSettled?: (results: { data?: T; error?: unknown }[]) => void;
};

export function useQueriesResolver<T = unknown, E = unknown>(
  queries: UseQueryResult<T, E>[],
  {
    onSuccess,
    onError,
    onSettled,
    onAllSuccess,
    onAllError,
    onAllSettled,
  }: MultiResolverCallbacks<T> = {},
) {
  const resolvedIndexesRef = useRef<Set<number>>(new Set());
  const allDoneFiredRef = useRef(false);

  useEffect(() => {
    const resolvedIndexes = resolvedIndexesRef.current;

    queries.forEach((query, idx) => {
      const { isFetched, isSuccess, isError, data, error } = query;

      if (!isFetched || resolvedIndexes.has(idx)) return;

      resolvedIndexes.add(idx);

      if (isSuccess && data !== undefined) {
        onSuccess?.(data, idx);
        onSettled?.(data, undefined, idx);
      } else if (isError && error !== undefined) {
        onError?.(error, idx);
        onSettled?.(undefined, error, idx);
      }
    });

    // Aggregate callbacks when all queries settle
    const allFetched = queries.every((q) => q.isFetched);
    if (allFetched && !allDoneFiredRef.current) {
      allDoneFiredRef.current = true;

      const results = queries.map((q) => ({
        data: q.isSuccess ? (q.data as T | undefined) : undefined,
        error: q.isError ? (q.error as unknown | undefined) : undefined,
      }));

      const allSucceeded = results.every((r) => r.data !== undefined);
      const allFailed = results.every((r) => r.error !== undefined);

      if (allSucceeded) {
        onAllSuccess?.(results.map((r) => r.data!) as T[]);
      } else if (allFailed) {
        onAllError?.(results.map((r) => r.error!));
      }

      onAllSettled?.(results);
    }
  }, [
    queries.length,
    ...queries.flatMap((q) => [
      q.isFetched,
      q.isSuccess,
      q.isError,
      q.data,
      q.error,
      q.dataUpdatedAt,
    ]),
  ]);

  return queries;
}